import React, { useState } from "react";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip, Tooltip } from "@mui/material";
import {
  Edit,
  Delete,
  DownloadIcon as Download,
  Document as Description,
  Document as PictureAsPdf,
  Document as Article,
  CodeFile as Code,
} from "components/icons";

const getStatusColor = (status) => {
  const colors = {
    DRAFT: "default",
    PUBLISHED: "success",
    ARCHIVED: "secondary",
    PENDING_REVIEW: "warning",
  };
  return colors[status] || "default";
};

const getCategoryColor = (category) => {
  const colors = {
    SOP: "primary",
    POLICY: "error",
    TEMPLATE: "info",
    GUIDE: "success",
    ARTICLE: "warning",
    OTHER: "default",
  };
  return colors[category] || "default";
};

const getFileIcon = (fileName) => {
  if (!fileName) return <Description />;
  const ext = fileName.split(".").pop().toLowerCase();
  if (ext === "pdf") return <PictureAsPdf color="error" />;
  if (["doc", "docx"].includes(ext)) return <Article color="primary" />;
  if (["xls", "xlsx"].includes(ext)) return <Description color="success" />;
  if (["js", "ts", "py", "java"].includes(ext)) return <Code color="warning" />;
  return <Description />;
};

export const DocumentDataGrid = ({ documents, isLoading, onEdit, onDelete, onDownload, showActions = true }) => {
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    ...(showActions ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 130,
      getActions: (params) => [
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="download" onClick={() => onDownload(params.row)} color="info" size="small">
          <Download />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>,
      ],
    }] : [{
      field: "download",
      type: "actions",
      headerName: "Download",
      width: 80,
      getActions: (params) => [
        <IconButton key="download" onClick={() => onDownload && onDownload(params.row)} color="info" size="small">
          <Download />
        </IconButton>,
      ],
    }]),
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "documentTitle",
      headerName: "Title",
      width: 250,
      flex: 1,
      renderCell: (params) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          {getFileIcon(params.row.fileName)}
          <span>{params.value}</span>
        </MDBox>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      width: 110,
      renderCell: (params) => (
        <Chip label={params.value} color={getCategoryColor(params.value)} size="small" />
      ),
    },
    { field: "author", headerName: "Author", width: 130 },
    {
      field: "createdAt",
      headerName: "Uploaded",
      width: 110,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : "N/A",
    },
    {
      field: "updatedAt",
      headerName: "Modified",
      width: 110,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value?.replace("_", " ")} color={getStatusColor(params.value)} size="small" />
      ),
    },
    { field: "version", headerName: "Version", width: 80 },
    {
      field: "tags",
      headerName: "Tags",
      width: 180,
      renderCell: (params) => (
        <MDBox display="flex" gap={0.5} flexWrap="wrap">
          {(params.value || []).slice(0, 2).map((tag, idx) => (
            <Chip key={idx} label={tag.tagName || tag} size="small" variant="outlined" />
          ))}
          {(params.value || []).length > 2 && (
            <Tooltip title={(params.value || []).slice(2).map(t => t.tagName || t).join(", ")}>
              <Chip label={`+${params.value.length - 2}`} size="small" variant="outlined" />
            </Tooltip>
          )}
        </MDBox>
      ),
    },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={documents || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id || row.documentId}
      />
    </MDBox>
  );
};

DocumentDataGrid.propTypes = {
  documents: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
  showActions: PropTypes.bool,
};

export default DocumentDataGrid;

